import classNames from 'helpers/class-names'
import Link from 'next/link'
import { FC, HTMLAttributes, ReactElement, ReactNode } from 'react'

interface LinkProps
  extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  isActive?: boolean
  className?: string
  textClassName?: string
  asLink?: boolean
  href?: string
  children?: ReactNode
  cyDataValue?: string
  type?: 'button' | 'submit' | 'reset'
}

const SubNavigationLink: FC<LinkProps> = ({
  isActive = false,
  onClick,
  className = '',
  textClassName = '',
  asLink = false,
  href,
  children,
  cyDataValue,
  type,
}) => {
  const styling = classNames(
    '-mb-px mr-6 rounded py-2 px-0 text-sm font-bold text-gray-300 text-center',
    'outline-none focus-visible:ring-2 focus-visible:ring-info/75 whitespace-nowrap',
    {
      'rounded-b-none border-b-4 border-info text-gray-500 dark:text-gray-100':
        isActive,
    },
    className,
  )
  const textStyling = classNames(
    'rounded-xl px-3 py-1 outline-none',
    'focus-visible:ring-2 focus-visible:ring-info/75',
    'transition-all duration-200 ease-in-out',
    {
      'hover:text-gray-500 hover:bg-gray-100/50': !isActive,
      block: asLink,
    },
    textClassName,
  )

  if (asLink) {
    return (
      <div className={styling}>
        <Link href={href}>
          <a
            role="tab"
            aria-selected={isActive}
            onClick={onClick}
            tabIndex={isActive ? -1 : 0}
            className={textStyling}
            data-cy={cyDataValue}>
            {children}
          </a>
        </Link>
      </div>
    )
  }

  return (
    <div className={styling}>
      <button
        role="tab"
        type={type}
        tabIndex={isActive ? -1 : 0}
        aria-selected={isActive}
        onClick={onClick}
        className={textStyling}
        data-cy={cyDataValue}>
        {children}
      </button>
    </div>
  )
}

interface Props {
  children:
    | Array<ReactElement<typeof SubNavigationLink>>
    | ReactElement<typeof SubNavigationLink>
  className?: string
}

const SubNavigation: FC<Props> = ({ children, className }) => (
  <div
    className={classNames(
      'mb-3 flex items-start border-b border-gray-100 dark:border-gray-600',
      className,
    )}>
    {children}
  </div>
)

export { SubNavigationLink as Link, SubNavigation as List }
